
import {
  Button, Modal, Icon, Spin, Checkbox,
} from 'ant-design-vue';
import {
  Component, Vue, Prop, Emit,
} from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import AliUpload from '@/utils/AliUpload';

const userModule = namespace('user');

interface WhiteboardItem {
  id: string | number; // 图片主键
  lessonId: string | number; // 讲次id
  classId: string | number; // 课程id
  createTime: string; // 创建时间
  updateTime: string; // 更新时间
  deleted: number; // 删除状态
  insId: string | number; // 机构id
  screenshotUrl: string; // 图片地址
  pageIndex: number; // 页码
  imageName: string; // 图片名称
  userId: string | number; // 用户id
  shouldLoad: boolean; // 是否需要加载
  loaded: boolean; // 图片是否已加载
  downloading: boolean; // 图片是否正在下载
  showPhoto: boolean; // 是否显示照片
}

@Component({
  components: {
    AModal: Modal,
    AIcon: Icon,
    AButton: Button,
    ASpin: Spin,
    ACheckbox: Checkbox,
  },
})
export default class BanshuDialog extends Vue {
  @Prop({ default: false }) readonly visible!: boolean;

  @userModule.Getter('userInfo') userInfo!: any;

  @Prop({ default: false }) readonly banshuLesson!: {
    lessonId: number;
    lessonName: string;
    classId: number;
    lessonNum: number;
  };

  // 板书列表
  private whiteboardList: WhiteboardItem[] = [];

  private visibleCount = 20; // 初始显示数量

  private loadStep = 10; // 每次加载数量

  private exporting = false; // 是否正在导出

  get visibleItems(): WhiteboardItem[] {
    return this.whiteboardList.slice(0, this.visibleCount);
  }

  get hasMore(): boolean {
    return this.visibleCount < this.whiteboardList.length;
  }

  // 选择性生成：选中的图片 id 列表（用数组保证 Vue 响应式）
  private selectedIds: (string | number)[] = [];

  get selectedItems(): WhiteboardItem[] {
    return this.whiteboardList.filter((item) => this.selectedIds.includes(item.id));
  }

  get isAllSelected(): boolean {
    return this.whiteboardList.length > 0 && this.selectedIds.length === this.whiteboardList.length;
  }

  private isSelected(id: string | number): boolean {
    return this.selectedIds.includes(id);
  }

  private toggleSelect(id: string | number, e?: Event) {
    if (e) e.stopPropagation();
    const idx = this.selectedIds.indexOf(id);
    if (idx === -1) {
      this.selectedIds = [...this.selectedIds, id];
    } else {
      this.selectedIds = this.selectedIds.filter((i) => i !== id);
    }
  }

  private toggleSelectAll(e?: Event) {
    if (e) e.stopPropagation();
    if (this.isAllSelected) {
      this.selectedIds = [];
    } else {
      this.selectedIds = this.whiteboardList.map((item) => item.id);
    }
  }

  // 预览弹层
  private previewVisible = false;

  private previewIndex = 0;

  get currentPreviewItem(): WhiteboardItem | null {
    if (this.previewIndex >= 0 && this.previewIndex < this.whiteboardList.length) {
      return this.whiteboardList[this.previewIndex];
    }
    return null;
  }

  @Emit('update:visible')
  private handleCancel() {
    return false;
  }

  // 初始化懒加载
  private initLazyLoad() {
    this.$nextTick(() => {
      if (this.visibleItems.length > 0) {
        this.loadInitialImages();
      }
    });
  }

  // 加载初始图片
  private loadInitialImages() {
    const initialCount = Math.min(this.visibleCount, this.visibleItems.length);
    for (let i = 0; i < initialCount; i += 1) {
      this.whiteboardList[i].shouldLoad = true;
    }
  }

  // 滚动处理 - 懒加载和无限滚动
  private handleScroll(event: Event) {
    const container = event.target as HTMLElement;
    const { scrollTop } = container;
    const { scrollHeight } = container;
    const { clientHeight } = container;

    // 懒加载图片
    this.lazyLoadImages(container);

    // 无限滚动 - 接近底部时加载更多
    if (scrollTop + clientHeight >= scrollHeight - 100 && this.hasMore) {
      this.loadMore();
    }
  }

  // 懒加载图片
  private lazyLoadImages(container: HTMLElement) {
    const items = container.querySelectorAll('.whiteboard-item');
    const containerRect = container.getBoundingClientRect();

    items.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect();
      const isVisible = itemRect.top < containerRect.bottom + 200 && itemRect.bottom > containerRect.top - 200;

      if (isVisible && index < this.whiteboardList.length) {
        this.whiteboardList[index].shouldLoad = true;
      }
    });
  }

  // 加载更多项目
  private loadMore() {
    const newCount = Math.min(this.visibleCount + this.loadStep, this.whiteboardList.length);
    this.visibleCount = newCount;
  }

  // 图片加载完成
  private onImageLoad(index: number) {
    if (index < this.whiteboardList.length) {
      this.whiteboardList[index].loaded = true;
    }
  }

  // 图片加载错误
  private onImageError(index: number) {
    if (index < this.whiteboardList.length) {
      this.whiteboardList[index].loaded = true;
      // 可以设置错误状态或默认图片
    }
  }

  // 打开预览
  private openPreview(index: number) {
    this.previewIndex = index;
    this.previewVisible = true;
  }

  // 关闭预览
  private closePreview() {
    this.previewVisible = false;
  }

  // 上一张
  private prevImage() {
    if (this.previewIndex > 0) {
      this.previewIndex -= 1;
    }
  }

  // 下一张
  private nextImage() {
    if (this.previewIndex < this.whiteboardList.length - 1) {
      this.previewIndex += 1;
    }
  }

  // 在预览中下载当前图片
  private downloadCurrentPreview() {
    const item = this.currentPreviewItem;
    if (item) {
      this.downloadImage(item);
    }
  }

  // 下载图片
  private async downloadImage(item: WhiteboardItem) {
    try {
      item.downloading = true;
      // 构建文件名：讲次序号-讲次名称-图片名称
      const fileName = `${this.banshuLesson.lessonNum}-${this.banshuLesson.lessonName}-${item.imageName}.png`;
      // 获取图片数据
      const response = await fetch(item.screenshotUrl);
      const blob = await response.blob();

      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;

      // 触发下载
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // 清理URL对象
      window.URL.revokeObjectURL(url);

      // 埋点 - 此组件拿不到$track，所以需要父组件
      this.$emit('clickTrack', {
        action: 'PRELESSONS_BANSHU_DOWNLOAD_CLICK',
        params: {
          id: item.id,
          name: item.imageName,
          pageIndex: item.pageIndex,
          lessonId: item.lessonId,
        },
      });
    } catch (error) {
      this.$message.error(`${this.banshuLesson.lessonName}${item.imageName} 下载失败`);
    } finally {
      item.downloading = false;
    }
  }

  private showPhoto = false;

  private mounted() {
    this.fetchList(); // 默认 showPhoto=false，所以默认拉板书
  }

  private async clickBanshu() {
    if (this.showPhoto === false) return;
    this.showPhoto = false;
    await this.fetchList();
  }

  private async clickPhoto() {
    if (this.showPhoto === true) return;
    this.showPhoto = true;
    await this.fetchList();
  }

  // 获取板书数据

  private async fetchList() {
    try {
      // 切换 tab 时重置懒加载/分页状态
      this.visibleCount = 30;
      this.whiteboardList = [];

      const expendObj = {
        shouldLoad: true,
        loaded: true,
        downloading: false,
      };

      const params = {
        lessonId: this.banshuLesson.lessonId,
        classId: this.banshuLesson.classId,
      };

      // 关键：根据 showPhoto 选择接口
      const res = this.showPhoto ? await this.$service.bk.getPhotoList(params) : await this.$service.bk.getBanshuList(params);

      this.whiteboardList = (res || []).map((item: any, index: number) => ({
        ...item,
        ...expendObj,
        // 关键：把合照的 photoUrl 也映射到 screenshotUrl
        screenshotUrl: item.screenshotUrl || item.photoUrl,
        // 合照没名字就给个默认
        imageName: item.imageName || `合照${index + 1}`,
      }));

      // 默认全选，用于选择性生成
      this.selectedIds = this.whiteboardList.map((item) => item.id);

      this.initLazyLoad();
    } catch (e) {
      // 可按需提示
    }
  }

  // 生成HTML并获取分享链接（仅包含选中的图片）
  private async handleGenerateHTML() {
    if (this.selectedItems.length === 0) {
      this.$message.warning('请至少选择一张图片');
      return;
    }

    try {
      this.exporting = true;
      this.$message.loading('正在生成HTML和分享链接...', 0);

      // 1. 生成 HTML 并下载到本地
      // 2. 上传 HTML 获取分享链接（内部会调用 postUploadOss）
      const shareUrl = await this.generateHTMLAndShare();

      this.$message.destroy();

      if (shareUrl) {
        // 显示分享链接
        const modal = Modal.confirm({
          title: '分享链接已生成',
          content: `分享链接：${shareUrl}`,
          okText: '复制链接',
          cancelText: '关闭',
          onOk: () => {
            this.copyToClipboard(shareUrl);
            this.$message.success('链接已复制到剪贴板');
            modal.destroy();
          },
          onCancel: () => {
            modal.destroy();
          },
        });
      } else {
        this.$message.success('HTML生成链接失败');
      }

      // 埋点
      this.$emit('clickTrack', {
        action: 'PRELESSONS_BANSHU_GENERATE_HTML',
        params: {
          lessonId: this.banshuLesson.lessonId,
          lessonName: this.banshuLesson.lessonName,
          imageCount: this.selectedItems.length,
        },
      });
    } catch (error) {
      this.$message.destroy();
      const errorMessage = error instanceof Error ? error.message : '生成失败，请重试';
      this.$message.error(errorMessage);
    } finally {
      this.exporting = false;
    }
  }

  // 生成HTML并获取分享URL
  private async generateHTMLAndShare(): Promise<string | null> {
    try {
      // 生成HTML内容
      const htmlContent = this.generateHTMLContent();

      // 尝试上传HTML到服务器获取分享URL
      try {
        const shareUrl = await this.uploadHTMLAndGetUrl(htmlContent);
        // console.log('分享链接:==============', shareUrl);
        if (shareUrl) {
          // console.log('分享链接已生成:', shareUrl);
        }
        return shareUrl;
      } catch (error) {
        // 如果上传失败，返回null，用户可以通过下载的HTML文件分享
        // console.error('上传HTML失败:', error);
        return null;
      }
    } catch (error) {
      throw new Error('生成HTML失败');
    }
  }

  // 生成HTML内容（单列布局，仅包含选中的图片）
  private generateHTMLContent(): string {
    const title = `第${this.banshuLesson.lessonNum}讲-${this.banshuLesson.lessonName}-课堂板书`;
    const items = this.selectedItems;
    const imagesHTML = items
      .map(
        (item, index) => `
        <div class="image-item">
          <h3>${item.imageName || `板书${index + 1}`}</h3>
          <img src="${item.screenshotUrl}" alt="${item.imageName}" loading="lazy" />
        </div>
      `,
      )
      .join('');

    return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
      background: #f5f5f5;
      padding: 20px;
      line-height: 1.6;
    }
    .container {
      max-width: 1000px;
      margin: 0 auto;
      background: #fff;
      padding: 30px;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }
    h1 {
      color: #333;
      margin-bottom: 30px;
      text-align: center;
      font-size: 24px;
      border-bottom: 2px solid #1890ff;
      padding-bottom: 15px;
    }
    .image-item {
      margin-bottom: 40px;
      page-break-inside: avoid;
      text-align: center;
    }
    .image-item h3 {
      color: #666;
      margin-bottom: 15px;
      font-size: 16px;
      font-weight: 500;
    }
    .image-item img {
      max-width: 100%;
      height: auto;
      display: block;
      margin: 0 auto;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    @media print {
      body {
        background: #fff;
        padding: 0;
      }
      .container {
        box-shadow: none;
        padding: 20px;
      }
    }
    @media (max-width: 768px) {
      body {
        padding: 10px;
      }
      .container {
        padding: 15px;
      }
    }
  </style>
</head>
<body>
  <div class="container">
    <h1>${title}</h1>
    ${imagesHTML}
  </div>
</body>
</html>`;
  }

  // 上传 HTML 到 OSS 并获取在线链接（直接使用 AliUpload.uploadCustomFile，拿 result.url）
  private async uploadHTMLAndGetUrl(htmlContent: string): Promise<string | null> {
    try {
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      const userId = this.userInfo?.userId ?? 'unknown';
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hour = String(now.getHours()).padStart(2, '0');
      const minute = String(now.getMinutes()).padStart(2, '0');
      const second = String(now.getSeconds()).padStart(2, '0');
      const timeStr = `${year}${month}${day}${hour}${minute}${second}`;
      const fileName = `${userId}_${timeStr}.html`;

      const ali = AliUpload.getInstance({
        getToken: () => this.$service.feedback.getUploadOssToken(),
      });
      await ali.init();
      const result = await ali.uploadCustomFile(fileName, blob, 'text/html');
      return result?.url ?? null;
    } catch (error) {
      // console.error('上传HTML失败:', error);
      return null;
    }
  }

  // 复制到剪贴板
  private copyToClipboard(text: string) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      // console.error('复制失败:', err);
    }
    document.body.removeChild(textarea);
  }
}
